/*
 * Users page.
 *
 * Master user roster. Sortable table of USERS joined to USER_SCORES with
 * a column picker that persists to localStorage (`rbx-users-cols`).
 * Optional columns expose less-common metadata dimensions (city,
 * country, state, company, ad group, manager). Clicking a row opens
 * the user detail drawer; clicking the risk pill opens the breakdown
 * modal.
 *
 * Calculations:
 *   - clickRate = signals.find(id='fail-rate').component * 100 from
 *     USER_SCORES, so the table and the score model never disagree.
 *   - completion % = COURSE_USER_COMPLETIONS[email] / total assigned.
 *   - lastLogin display = lastLoginDays formatted as 'today',
 *     'yesterday', 'Xd ago'.
 *
 * Postgres mapping:
 *   - canonical `users` joined to `user_risk_scores` (latest computed)
 *     and `course_user_completions_v` materialised view.
 *   - server-side pagination + sort once the tenancy exceeds ~2k users.
 *
 * localStorage keys: `rbx-users-cols` JSON array of visible column ids.
 *
 * Integration hook: `useUsersQuery({ sortBy, direction, limit, offset,
 * scope })` returning { rows, total } cache 1h.
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Popover, Checkbox, FormControlLabel } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MainCard from '../../../components/cards/MainCard';
import { USERS, DATA_EPOCH } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import { USER_SCORES, BANDS } from '../riskScore';
import RiskBreakdownModal from './RiskBreakdownModal';
import ActiveFilters from './ActiveFilters';
import { FilterChip } from './MetaNav';

// Combined Summary table, replaces the old Engaged Users widget per the
// May 2026 review. One large table with click rate, completion, risk score
// and email side-by-side, plus a tick-box column picker for the tenancy's
// metadata fields. Engaged/idle is preserved as a filter.

// Left-aligned "View" affordance for drillable rows: replaces whole-row
// click so the click target is explicit, matching the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

// Lazy + epoch-tracked so the score map follows the live hydrated data.
let scoreCache = null;
let scoreEpoch = -1;
const SCORE_BY_EMAIL = {
    get(email) {
        if (scoreEpoch !== DATA_EPOCH) {
            scoreCache = new Map(USER_SCORES.map((u) => [u.email, u]));
            scoreEpoch = DATA_EPOCH;
        }
        return scoreCache.get(email);
    },
};

const COLUMNS = [
    { id: 'name',        label: 'Name',          always: true,    sortable: true,  type: 'string', accessor: (u) => u.name },
    { id: 'email',       label: 'Email',         always: true,    sortable: true,  type: 'string', accessor: (u) => u.email },
    { id: 'dept',        label: 'Department',    defaultOn: true, sortable: true,  type: 'string', accessor: (u) => u.dept },
    { id: 'location',    label: 'Location',      defaultOn: true, sortable: true,  type: 'string', accessor: (u) => u.location },
    { id: 'manager',     label: 'Line manager',  sortable: true,  type: 'string',  accessor: (u) => u.manager },
    { id: 'country',     label: 'Country',       sortable: true,  type: 'string',  accessor: (u) => u.country },
    { id: 'city',        label: 'City',          sortable: true,  type: 'string',  accessor: (u) => u.city },
    { id: 'company',     label: 'Company',       sortable: true,  type: 'string',  accessor: (u) => u.company },
    { id: 'adGroup',     label: 'AD group',      sortable: true,  type: 'string',  accessor: (u) => u.adGroup },
    { id: 'simsSent',       label: 'Sims sent',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.simsSent ?? null, format: (v) => v == null ? '—' : v, align: 'right' },
    { id: 'clickRate',   label: 'Click rate',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => clickRate(u), format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'secondStagePct', label: '2nd stage',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.secondStagePct ?? null, format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'reportedPct',    label: 'Reported',     defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.reportedPct ?? null, format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'coursesSent',    label: 'Courses sent', defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.coursesSent ?? null, format: (v) => v == null ? '—' : v, align: 'right' },
    { id: 'completion',  label: 'Completion',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.comp, format: (v) => `${v}%`, align: 'right' },
    { id: 'avgScore',       label: 'Avg score',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.avgScore ?? null, format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'riskScore',   label: 'Risk score',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => (u._server ? u._riskScore : SCORE_BY_EMAIL.get(u.email)?.finalScore ?? null), align: 'right' },
    { id: 'status',      label: 'Status',        sortable: true,  type: 'string',  accessor: (u) => u.disabled ? 'Disabled' : 'Active' },
];

function clickRate(u) {
    if (u && u._server) return u._clickRate;
    const score = SCORE_BY_EMAIL.get(u.email);
    if (!score || score.insufficient) return null;
    const fr = score.signals.find((s) => s.id === 'fail-rate');
    return fr ? Math.round(fr.component * 100) : null;
}

function compareValues(a, b, type) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (type === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

function defaultCols() {
    return COLUMNS.filter((c) => c.always || c.defaultOn).map((c) => c.id);
}

function loadCols() {
    try {
        const stored = JSON.parse(localStorage.getItem('rbx-users-cols'));
        if (Array.isArray(stored) && stored.length > 0) return stored;
    } catch { /* fall through */ }
    return defaultCols();
}

function ColumnPicker({ active, setActive }) {
    const [anchor, setAnchor] = useState(null);
    const toggle = (id) => {
        const col = COLUMNS.find((c) => c.id === id);
        if (col?.always) return;
        const next = active.includes(id) ? active.filter((x) => x !== id) : [...active, id];
        setActive(next);
        localStorage.setItem('rbx-users-cols', JSON.stringify(next));
    };
    return (
        <>
            <Button
                size="small" variant="outlined"
                startIcon={<ViewColumnIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ borderRadius: '4px', borderColor: 'divider', color: 'text.secondary', fontSize: 11, py: '3px', px: '10px', textTransform: 'none', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}
            >
                Columns ({active.length})
            </Button>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1, mt: 0.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '8px', minWidth: 200 } }}
            >
                <Box sx={{ px: 1, py: 0.5, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                    Columns
                </Box>
                {COLUMNS.map((c) => (
                    <FormControlLabel
                        key={c.id}
                        control={
                            <Checkbox
                                checked={active.includes(c.id)}
                                disabled={c.always}
                                onChange={() => toggle(c.id)}
                                size="small"
                                sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: 'secondary.main' } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 12, color: c.always ? 'text.disabled' : 'text.primary' }}>{c.label}</Typography>}
                        sx={{ display: 'flex', m: 0, px: 1, py: 0.25, borderRadius: '4px', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                ))}
            </Popover>
        </>
    );
}

function ScoreCell({ value }) {
    if (value == null) return <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>, </Typography>;
    const band = BANDS.find((b) => value >= b.min && value <= b.max) || BANDS[BANDS.length - 1];
    return (
        <Box
            sx={{
                display: 'inline-flex', alignItems: 'center',
                px: '8px', py: '2px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 11, fontWeight: 800, letterSpacing: '-0.01em',
                bgcolor: band.bg, color: band.textColor,
                transition: 'transform 120ms',
                '&:hover': { transform: 'translateY(-1px)' },
            }}
        >
            {value}
        </Box>
    );
}

function StatusCell({ value }) {
    const active = value === 'Active';
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: '6px', py: '1px', borderRadius: '4px',
            fontSize: 10, fontWeight: 600,
            color: active ? '#388E3C' : 'text.disabled',
            bgcolor: active ? 'rgba(102,187,106,.08)' : 'action.hover',
            border: '1px solid', borderColor: active ? 'rgba(102,187,106,.25)' : 'divider',
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: active ? '#66BB6A' : 'text.disabled' }} />
            {value}
        </Box>
    );
}

const PAGE_SIZES = [50, 100, 200];

export default function UsersPage({ go, drill, scope, removeScope, initialSortBy, initialSortDir }) {
    // Sort can be seeded by drill context (e.g. the Courses Users card lands
    // here ordered by completion ascending); fully editable after landing.
    const [sortBy, setSortBy] = useState(initialSortBy || 'riskScore');
    const [sortDir, setSortDir] = useState(initialSortDir || 'desc');
    const [active, setActive] = useState(loadCols);
    const [excludeDisabled, setExcludeDisabled] = useState(true);
    const [pageSize, setPageSize] = useState(50);
    const [modal, setModal] = useState(null);

    // Server-side roster: sort/filters/scope are computed over EVERY user in SQL (the
    // raw client roster caps at 20k rows and big tenancies exceed it, so the client table was
    // silently incomplete). Falls back to the client path when the aggregate is unavailable.
    const s0 = scope || {};
    const normScope = (v) => (v == null ? undefined : Array.isArray(v) ? (v.length ? v : undefined) : [v]);
    const serverScope = { department: normScope(s0.department), location: normScope(s0.location), city: normScope(s0.city), country: normScope(s0.country), state: normScope(s0.state), company: normScope(s0.company), adGroup: normScope(s0.adGroup), user: normScope(s0.user) };
    const hasServerScope = Object.values(serverScope).some(Boolean);
    // a user scope holding display NAMES can't resolve server-side (server matches emails) - use the client path
    const userScopeIsEmails = !serverScope.user || serverScope.user.every((v) => String(v).includes('@'));
    const rosterQ = useDashboardData({
        metric: 'users', aggregation: 'user_roster', limit: pageSize,
        sort: { field: sortBy, direction: sortDir },
        includeDisabled: !excludeDisabled,
        scope: hasServerScope ? serverScope : undefined,
    }, { skip: !userScopeIsEmails });
    const serverTotal = rosterQ.data?.meta?.totalRecords;
    const serverRows = (userScopeIsEmails && Array.isArray(rosterQ.data?.roster)) ? rosterQ.data.roster.map((r) => ({
        name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email,
        email: r.email, dept: r.dept, location: r.location, city: r.city, country: r.country,
        state: r.state, company: r.company, adGroup: r.adGroup, manager: r.manager,
        disabled: !r.enabled, lastLoginDays: r.lastLoginDays, comp: r.completionPct ?? 0,
        simsSent: r.simsSent ?? null, secondStagePct: r.secondStagePct ?? null, reportedPct: r.reportedPct ?? null,
        coursesSent: r.coursesSent ?? null, avgScore: r.avgScore ?? null,
        _server: true, _clickRate: r.clickRate ?? null, _riskScore: r.riskScore ?? null,
        // Raw server risk signals for the score breakdown modal - the client
        // model can't reproduce the EWMA'd server score, so the modal renders
        // these instead when the row came from the roster aggregation.
        _factors: { failRate: r.failRate, repeatRate: r.repeatRate, stageDepth: r.stageDepth, trainingDebt: r.trainingDebt, courseLag: r.courseLag, baseScore: r.baseScore, simsInWindow: r.simsSent, coursesSent: r.coursesSent, finalScore: r.riskScore, band: r.band },
    })) : null;

    const filtered = useMemo(() => {
        const s = scope || {};
        const isIn = (val, vs) => !vs || (Array.isArray(vs) ? vs.includes(val) : val === vs);
        return USERS.filter((u) => {
            if (excludeDisabled && u.disabled) return false;
            if (!isIn(u.dept,     s.department)) return false;
            if (!isIn(u.location, s.location))   return false;
            if (!isIn(u.country,  s.country))    return false;
            if (s.user) {
                const arr = Array.isArray(s.user) ? s.user : [s.user];
                if (!arr.some((v) => u.email === v || u.name === v)) return false;
            }
            return true;
        });
    }, [scope, excludeDisabled]);

    const sorted = useMemo(() => {
        const col = COLUMNS.find((c) => c.id === sortBy);
        if (!col) return filtered;
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => compareValues(col.accessor(a), col.accessor(b), col.type) * dir);
    }, [filtered, sortBy, sortDir]);

    const rows = serverRows || sorted;
    const totalCount = serverRows ? (serverTotal ?? serverRows.length) : sorted.length;
    const visible = rows.slice(0, pageSize);
    const activeCols = COLUMNS.filter((c) => active.includes(c.id));

    const requestSort = (id) => {
        if (sortBy === id) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortBy(id); setSortDir(id === 'name' || id === 'email' ? 'asc' : 'desc'); }
    };

    // Filter picker + chips row, consistent with the Sims/Courses pages: a
    // user autocomplete adds a scope chip via drill, and any drilled-in scope
    // (dept, course, group, ...) shows as removable chips here like everywhere
    // else - previously the roster applied scope invisibly.
    const userScopeVals = (() => { const v = (scope || {}).user; return v == null ? [] : Array.isArray(v) ? v : [v]; })();
    const userPickerOptions = useMemo(
        () => USERS.map((u) => ({ label: u.email ? `${u.name} (${u.email})` : u.name, value: u.email || u.name, display: u.name })).sort((a, b) => a.label.localeCompare(b.label)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DATA_EPOCH],
    );

    return (
        <motion.div className="page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <FilterChip
                    label="User" options={userPickerOptions} activeValues={userScopeVals} searchPlaceholder="Search users"
                    onApply={({ added, removed }) => {
                        removed.forEach((v) => removeScope && removeScope('user', v));
                        added.forEach((v) => drill && drill('users', userPickerOptions.find((o) => o.value === v)?.display || v, { type: 'user', value: v }));
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
                </Box>
            </Box>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                <div>
                    <div className="ptitle">Users</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>One combined table across sims, courses and risk - click a row for activity, click a score for breakdown</div>
                </div>
            </div>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                {/* Toolbar. Search removed (the Filter-by-user picker covers finding a
                    user, matching the other pages); the engaged/idle toggle removed per
                    review - engagement is covered by the sims/courses columns now. */}
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={excludeDisabled}
                                onChange={(e) => setExcludeDisabled(e.target.checked)}
                                size="small"
                                sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: 'secondary.main' } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Exclude disabled users</Typography>}
                        sx={{ m: 0 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        {visible.length >= totalCount ? `${totalCount.toLocaleString()} users` : `${visible.length} of ${totalCount.toLocaleString()} users`}
                    </Typography>
                    <ColumnPicker active={active} setActive={setActive} />
                </Box>

                {/* Table */}
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '1px' }} />
                                {activeCols.map((c) => {
                                    const isSorted = sortBy === c.id;
                                    return (
                                        <Box
                                            key={c.id}
                                            component="th"
                                            onClick={() => c.sortable && requestSort(c.id)}
                                            sx={{
                                                fontSize: 11, fontWeight: 600, color: isSorted ? 'text.primary' : 'text.disabled',
                                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                                px: 2, py: 1.25, textAlign: c.align || 'left',
                                                borderBottom: '1px solid', borderColor: 'divider',
                                                whiteSpace: 'nowrap',
                                                cursor: c.sortable ? 'pointer' : 'default',
                                                userSelect: 'none',
                                                '&:hover': c.sortable ? { color: 'text.primary' } : undefined,
                                            }}
                                        >
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                                {c.label}
                                                {c.sortable && (
                                                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 0.25 }}>
                                                        <ArrowDropUpIcon sx={{ fontSize: 12, m: '-3px 0', color: isSorted && sortDir === 'asc' ? 'secondary.main' : 'text.disabled' }} />
                                                        <ArrowDropDownIcon sx={{ fontSize: 12, m: '-3px 0', color: isSorted && sortDir === 'desc' ? 'secondary.main' : 'text.disabled' }} />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {visible.length === 0 && (
                                <Box component="tr">
                                    <Box component="td" colSpan={activeCols.length + 1} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>No users match current filters</Box>
                                </Box>
                            )}
                            {visible.map((u) => (
                                <Box
                                    component="tr"
                                    key={u.email}
                                    sx={{ borderBottom: '1px solid', borderColor: 'divider', transition: 'background 150ms', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}
                                >
                                    <Box component="td" sx={{ px: 2, py: 1.25, width: '1px', whiteSpace: 'nowrap' }}>
                                        <RowViewButton onClick={() => drill('user-detail', u.name, { type: 'user', value: u.email })} />
                                    </Box>
                                    {activeCols.map((c) => {
                                        const raw = c.accessor(u);
                                        const align = c.align || 'left';
                                        if (c.id === 'riskScore') {
                                            return (
                                                <Box component="td" key={c.id} sx={{ px: 2, py: 1.25, textAlign: align }} onClick={(e) => { e.stopPropagation(); if (raw != null) setModal({ email: u.email, name: u.name, dept: u.dept, factors: u._factors }); }}>
                                                    <ScoreCell value={raw} />
                                                </Box>
                                            );
                                        }
                                        if (c.id === 'status') {
                                            return (
                                                <Box component="td" key={c.id} sx={{ px: 2, py: 1.25, textAlign: align }}>
                                                    <StatusCell value={raw} />
                                                </Box>
                                            );
                                        }
                                        const display = typeof c.format === 'function' ? c.format(raw) : raw ?? ', ';
                                        const isName = c.id === 'name';
                                        return (
                                            <Box component="td" key={c.id} sx={{
                                                px: 2, py: 1.25,
                                                textAlign: align,
                                                color: isName ? 'text.primary' : 'text.secondary',
                                                fontWeight: isName ? 600 : 400,
                                                fontSize: c.id === 'email' ? 11 : 12,
                                                whiteSpace: c.id === 'email' ? 'nowrap' : 'normal',
                                            }}>
                                                {display}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mr: 0.5 }}>Per page</Typography>
                        {PAGE_SIZES.map((n) => {
                            const isActive = pageSize === n;
                            return (
                                <Button key={n} size="small" onClick={() => setPageSize(n)} sx={{
                                    minWidth: 0, fontSize: 11, fontWeight: 600, px: 1.25, py: '3px', borderRadius: '4px',
                                    border: '1px solid', borderColor: isActive ? 'secondary.main' : 'divider',
                                    bgcolor: isActive ? 'secondary.main' : 'transparent',
                                    color: isActive ? 'common.white' : 'text.secondary',
                                    '&:hover': { borderColor: 'secondary.main', bgcolor: isActive ? 'secondary.main' : 'transparent' },
                                }}>{n}</Button>
                            );
                        })}
                    </Box>
                </Box>
            </MainCard>

            <RiskBreakdownModal
                open={!!modal}
                onClose={() => setModal(null)}
                email={modal?.email}
                name={modal?.name}
                dept={modal?.dept}
                serverFactors={modal?.factors}
                go={go}
            />
        </motion.div>
    );
}
